import { useId } from 'react';

import Checkbox from '@cartwhl/ui/atoms/Checkbox';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import Text from '@cartwhl/ui/base/Text';
import TextNew from '@cartwhl/ui/base/TextNew';
import CheckedIcon from '@cartwhl/ui/icons/CheckedIcon';
import UncheckedIcon from '@cartwhl/ui/icons/UncheckedIcon';
import CheckboxDropdownButton from '@cartwhl/ui/input/CheckboxDropdownButton';

import { ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS } from '../feature-flags';
import { isUserKeyPoseRecord } from '../keyPoses/keyPoseSelectors';
import {
  useStore as useKeyPoseStore,
  useStoreApi as useKeyPoseStoreApi,
} from '../keyPoses/keyPoseStore';

interface KeyPoseGhostVisibilityToggleProps {
  hasMotionToPlay: boolean;
  sceneReferenceNames: readonly string[];
}

export default function KeyPoseGhostVisibilityToggle({
  hasMotionToPlay,
  sceneReferenceNames,
}: KeyPoseGhostVisibilityToggleProps) {
  const baseId = useId();
  const keyPoseStore = useKeyPoseStore();
  const keyPoseStoreApi = useKeyPoseStoreApi();
  const keyPoseRecords = sceneReferenceNames.flatMap((referenceName) => {
    const referenceState = keyPoseStore.state.referencesByName[referenceName];
    return (
      referenceState?.keyPoseIds.flatMap((keyPoseId) => {
        const keyPose = keyPoseStore.state.keyPosesById[keyPoseId];
        return keyPose ? [keyPose] : [];
      }) ?? []
    );
  });
  const visibleKeyPoseCount = keyPoseRecords.filter(
    (keyPose) => isUserKeyPoseRecord(keyPose) && !keyPose.hidden,
  ).length;
  const hasVisibleKeyPoseGhosts = visibleKeyPoseCount > 0;
  const canToggleKeyPoseVisibility = keyPoseRecords.length > 0;
  const showOnlyCurrentActorGhosts =
    keyPoseStore.state.ghostOptions.showOnlyCurrentActor;
  const showCurrentActorGhostOption = sceneReferenceNames.length > 1;
  const useActorColorGhosts = keyPoseStore.state.ghostOptions.useActorColors;
  const useOnionSkinKeyPoseGhosts =
    keyPoseStore.state.ghostOptions.onionSkin.enabled;
  const { showControlFrameGhosts } = keyPoseStore.state.controlFrameSettings;
  const keyPoseVisibilityTooltip = hasVisibleKeyPoseGhosts
    ? 'Hide all key pose ghosts'
    : 'Show all key pose ghosts';

  const handleKeyPoseGhostsVisibleChange = (checked: boolean) => {
    keyPoseStore.clearHoverPreview();
    if (checked) {
      for (const referenceName of sceneReferenceNames) {
        keyPoseStore.showKeyPosesForReference({ referenceName });
      }
      return;
    }

    for (const referenceName of sceneReferenceNames) {
      keyPoseStore.hideKeyPosesForReference({ referenceName });
    }
  };

  return (
    <Tooltip text={keyPoseVisibilityTooltip}>
      <span className="inline-flex">
        <CheckboxDropdownButton
          checked={hasVisibleKeyPoseGhosts}
          aria-label={keyPoseVisibilityTooltip}
          disabled={!hasMotionToPlay || !canToggleKeyPoseVisibility}
          dropdownAriaLabel="Key pose ghost options"
          dropdownDisabled={!hasMotionToPlay}
          dropdownWidth={320}
          dropdownContent={
            <div className="flex flex-col px-2 py-4">
              <GhostDropdownDescription
                title="Show ghosts"
                description="Shows your key pose ghosts in the 3D view as a reference while editing motion."
              />
              <div className="border-primary mt-3 flex flex-col gap-3 border-t pt-3">
                <GhostDropdownCheckboxOption
                  id={`${baseId}-control-frame-ghosts`}
                  title="Show control frame ghosts"
                  description="Shows auto-generated control frames as lighter orange ghosts in the 3D view."
                  checked={showControlFrameGhosts}
                  disabled={!hasMotionToPlay}
                  onCheckedChange={(checked) => {
                    keyPoseStoreApi.setControlFrameSettings({
                      showControlFrameGhosts: checked,
                    });
                  }}
                />
                {ENABLE_MOTION_EDITOR_ADVANCED_GHOST_OPTIONS ? (
                  <div className="border-primary flex flex-col border-t pt-3">
                    <KeyPoseGhostOptionRow
                      checked={useOnionSkinKeyPoseGhosts}
                      label="Onion skin"
                      onCheckedChange={(checked) =>
                        keyPoseStore.setKeyPoseOnionSkinGhostsEnabled(checked)
                      }
                    />
                    <KeyPoseGhostOptionRow
                      checked={useActorColorGhosts}
                      label="Actor colors"
                      onCheckedChange={(checked) =>
                        keyPoseStore.setUseActorColorGhosts(checked)
                      }
                    />
                    {showCurrentActorGhostOption ? (
                      <KeyPoseGhostOptionRow
                        checked={showOnlyCurrentActorGhosts}
                        label="Hide other actors"
                        onCheckedChange={(checked) =>
                          keyPoseStore.setShowOnlyCurrentActorGhosts(checked)
                        }
                      />
                    ) : null}
                  </div>
                ) : null}
              </div>
            </div>
          }
          onCheckedChange={handleKeyPoseGhostsVisibleChange}
        >
          Show ghosts
        </CheckboxDropdownButton>
      </span>
    </Tooltip>
  );
}

function GhostDropdownDescription({
  title,
  description,
}: {
  title: string;
  description: string;
}) {
  return (
    <div className="max-w-[20rem] text-left">
      <div className="ml-4 flex min-w-0 flex-col items-start gap-0.5">
        <Text as="span" variant="studio/body/medium/sm">
          {title}
        </Text>
        <Text
          as="span"
          variant="studio/body/sm"
          className="text-gray-45 font-normal"
        >
          {description}
        </Text>
      </div>
    </div>
  );
}

function GhostDropdownCheckboxOption({
  id,
  title,
  description,
  checked,
  disabled,
  onCheckedChange,
}: {
  id: string;
  title: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onCheckedChange: (checked: boolean) => void;
}) {
  return (
    <div className="flex max-w-[20rem] items-start gap-2 text-left">
      <Checkbox
        color="settings"
        id={id}
        variant="checkbox"
        align="start"
        className="mt-0.5"
        checked={checked}
        disabled={disabled}
        setChecked={onCheckedChange}
      />
      <label
        htmlFor={id}
        className={`flex min-w-0 flex-col items-start gap-0.5 ${
          disabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer'
        }`}
      >
        <Text as="span" variant="studio/body/medium/sm">
          {title}
        </Text>
        <Text
          as="span"
          variant="studio/body/sm"
          className="text-gray-45 font-normal"
        >
          {description}
        </Text>
      </label>
    </div>
  );
}

function KeyPoseGhostOptionRow({
  checked,
  disabled = false,
  label,
  onCheckedChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onCheckedChange?: (checked: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="checkbox"
      aria-checked={checked}
      aria-disabled={disabled}
      className={`flex h-10 w-full max-w-[20rem] items-center gap-4 rounded-lg px-4 text-left transition-colors ${
        disabled
          ? 'cursor-not-allowed opacity-50'
          : 'hover:bg-secondary cursor-pointer'
      }`}
      disabled={disabled}
      onClick={() => onCheckedChange?.(!checked)}
    >
      <span
        aria-hidden
        className="flex h-5 w-5 flex-none items-center justify-center"
      >
        {checked ? (
          <CheckedIcon className="text-blue h-5 w-5" />
        ) : (
          <UncheckedIcon className="text-tertiary h-5 w-5" />
        )}
      </span>
      <TextNew token="14/medium" className="text-primary truncate">
        {label}
      </TextNew>
    </button>
  );
}
